import React, { useState } from "react";
import { message } from "antd";
import { useModalStore } from "../../../store/slices/modal";
import {
  useCategoryCreate,
  useCategoryDelete,
  useCategoryUpdate,
} from "../../../hooks/api/category/hooks";
import { ICategory } from "../../../types/api/category";
import { useCategoryTableState } from "./useCategoryTableState";
import { CategoryPanel } from "./CategoryPanel";
import { CategoryFormModal, CategoryFormValues } from "./CategoryFormModal";
import { MODAL_TYPES } from "../../../types/modal-types";

import { useMessageApi } from "../../../provider/AntdNotification";

const CategoryList: React.FC = () => {
  const messageApi = useMessageApi();
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const { type, data, open, close } = useModalStore();
  const isModalOpen =
    type === MODAL_TYPES.CREATE_CATEGORY || type === MODAL_TYPES.EDIT_CATEGORY;

  const editingCategory: ICategory | null =
    type === MODAL_TYPES.EDIT_CATEGORY ? (data as ICategory) : null;
  const createParentId: string | null =
    type === MODAL_TYPES.CREATE_CATEGORY
      ? ((data as any)?.parentId ?? null)
      : null;
  const modalParentId = editingCategory?.parentId ?? createParentId ?? null;

  const parent = useCategoryTableState({ parentId: "null" });
  const child = useCategoryTableState({ parentId: selectedParentId });

  const activeQueryData = modalParentId ? child.queryData : parent.queryData;

  const { mutate: createCategory, isPending: creating } = useCategoryCreate({
    qData: activeQueryData || undefined,
    onSuccess: () => {
      messageApi.success("Category created.");
      close();
    },
    onError: (err: any) => messageApi.error(err?.response?.data?.message ?? err?.message ?? "Failed to create category"),
  });

  const { mutate: updateCategory, isPending: updating } = useCategoryUpdate({
    qData: activeQueryData || undefined,
    onSuccess: () => {
      messageApi.success("Category updated.");
      close();
    },
    onError: (err: any) => messageApi.error(err?.response?.data?.message ?? err?.message ?? "Failed to update category"),
  });

  const { mutate: deleteCategory } = useCategoryDelete({
    onSuccess: () => {
      messageApi.success("Category deleted.");
      if (parent.queryData) parent.refetch(parent.queryData);
      if (child.queryData) child.refetch(child.queryData);
    },
    onError: (err: any) =>
      messageApi.error(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to delete category.",
      ),
  });

  const handleAddParent = () => open(MODAL_TYPES.CREATE_CATEGORY, null);

  const handleAddChild = () =>
    selectedParentId &&
    open(MODAL_TYPES.CREATE_CATEGORY, { parentId: selectedParentId });

  const handleEdit = (record: ICategory) =>
    open(MODAL_TYPES.EDIT_CATEGORY, record);

  const handleFormSubmit = (values: CategoryFormValues) => {
    if (editingCategory?._id) {
      updateCategory({ _id: editingCategory._id, ...values });
    } else {
      createCategory(values);
    }
  };

  const selectedParentName = parent.list.find(
    (c) => c._id === selectedParentId,
  )?.name;

  return (
    <div className=" min-h-screen">
      <div className="w-full">
        <CategoryPanel
          title="Categories"
          addLabel="Add Category"
          isParent
          list={parent.list}
          total={parent.total}
          loading={parent.isLoading}
          isError={parent.isError}
          queryData={parent.queryData}
          search={parent.search}
          onSearchChange={parent.setSearch}
          statusFilter={parent.statusFilter}
          onStatusChange={parent.setStatusFilter}
          sortBy={parent.sortBy}
          onSortChange={parent.setSortBy}
          onPageChange={parent.setPage}
          onAdd={handleAddParent}
          selectedId={selectedParentId}
          onRowClick={(record) => setSelectedParentId(record._id)}
          onEdit={handleEdit}
          onDelete={deleteCategory}
        />

        {/* Re-enabled (Frankie, Oct 2026: "is there anywhere I can get in
            to edit sub categories please?") - this panel and its wiring
            (handleAddChild, the `child` table state, CategoryFormModal's
            parentId handling) were already fully built, just commented out
            since an earlier decision to go single-level-only. Select a
            parent row above to manage its subcategories here. */}
        {selectedParentId && (
          <CategoryPanel
            title={
              <div className="flex">
                <p className="text-sm font-medium">Subcategories</p>
                {selectedParentName && (
                  <span className="ml-2 text-sm font-normal text-slate-500">
                    (for {selectedParentName})
                  </span>
                )}
              </div>
            }
            addLabel="Add Subcategory"
            isParent={false}
            list={selectedParentId ? child.list : []}
            total={selectedParentId ? child.total : 0}
            loading={selectedParentId ? child.isLoading : false}
            isError={selectedParentId ? child.isError : false}
            queryData={selectedParentId ? child.queryData : null}
            search={child.search}
            onSearchChange={child.setSearch}
            statusFilter={child.statusFilter}
            onStatusChange={child.setStatusFilter}
            onPageChange={child.setPage}
            onAdd={handleAddChild}
            addDisabled={!selectedParentId}
            emptyText={
              selectedParentId
                ? "No subcategories found."
                : "Select a parent category first."
            }
            onEdit={handleEdit}
            onDelete={deleteCategory}
          />
        )}
      </div>

      <CategoryFormModal
        open={isModalOpen}
        onClose={close}
        onSubmit={handleFormSubmit}
        submitting={creating || updating}
        editingCategory={editingCategory}
        parentId={modalParentId}
        parentName={selectedParentName}
      />
    </div>
  );
};

export default CategoryList;
